import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, TrendingUp, Home, Users, DollarSign, PieChart, Building } from 'lucide-react';

const InvestorBuilds = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#262626] to-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Build Wealth Through Strategic Real Estate Development
            </h1>
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Partner with EC Home Builds to create investment properties that generate strong returns. From single-family homes to duplexes, we provide the expertise and transparency you need to succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white text-lg px-8 py-6">
                  Discuss Investment Opportunities
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/cost-estimate">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#262626] text-lg px-8 py-6">
                  Calculate ROI
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Models */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Investment Models
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple strategies to build your real estate portfolio and generate wealth
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Home,
                title: 'Build to Sell',
                description: 'Construct spec homes for immediate sale. Capture equity gains of 10-20% upon completion.',
                benefits: ['Quick turnaround', 'Immediate profit', 'Market appreciation']
              },
              {
                icon: Building,
                title: 'Build to Rent',
                description: 'Create rental properties for long-term cash flow and appreciation in Central Ohio\'s strong rental market.',
                benefits: ['Monthly income', 'Tax advantages', 'Long-term wealth']
              },
              {
                icon: Users,
                title: 'Duplex Development',
                description: 'Maximize land value with multi-family properties. Live in one unit, rent the other, or rent both.',
                benefits: ['Double income', 'Lower cost per unit', 'Strong demand']
              },
              {
                icon: TrendingUp,
                title: 'Spec Partnerships',
                description: 'Partner with EC Home Builds on spec developments. We bring expertise, you bring capital.',
                benefits: ['Shared risk', 'Professional execution', 'Proven systems']
              }
            ].map((model, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-[#DAA520]/10 rounded-full flex items-center justify-center mb-4">
                    <model.icon className="text-[#DAA520]" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{model.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{model.description}</p>
                  <ul className="space-y-2">
                    {model.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-[#DAA520] rounded-full mr-2"></span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Example Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Example Investment Scenarios
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real numbers from Central Ohio market conditions (estimates for illustration)
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Single-Family Spec Home',
                sqft: '1,864 sq. ft.',
                plan: 'The Cedar Point',
                investment: '$350,000',
                timeline: '5-6 months',
                arv: '$420,000',
                profit: '$70,000',
                roi: '20%',
                strategy: 'Build to Sell'
              },
              {
                title: 'Rental Property',
                sqft: '1,627 sq. ft.',
                plan: 'The Harrison',
                investment: '$320,000',
                timeline: '5-6 months',
                rent: '$2,200/mo',
                cashFlow: '$400/mo',
                roi: '15% annual',
                strategy: 'Build to Rent'
              },
              {
                title: 'Investment Duplex',
                sqft: '3,770 sq. ft. total',
                plan: 'The Independence',
                investment: '$550,000',
                timeline: '5-6 months',
                rent: '$4,000/mo',
                cashFlow: '$800/mo',
                roi: '18% annual',
                strategy: 'Multi-Family'
              }
            ].map((scenario, index) => (
              <Card key={index} className="border-2 border-[#DAA520] hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="bg-[#DAA520] text-white px-3 py-1 rounded-full text-sm font-semibold inline-block mb-4">
                    {scenario.strategy}
                  </div>
                  <h3 className="text-2xl font-bold text-[#262626] mb-2">{scenario.title}</h3>
                  <p className="text-gray-600 mb-6">{scenario.plan} • {scenario.sqft}</p>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Investment</span>
                      <span className="font-bold text-[#262626]">{scenario.investment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timeline</span>
                      <span className="font-semibold text-gray-700">{scenario.timeline}</span>
                    </div>
                    {scenario.arv && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">After Repair Value</span>
                        <span className="font-bold text-[#262626]">{scenario.arv}</span>
                      </div>
                    )}
                    {scenario.rent && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Rent</span>
                        <span className="font-bold text-[#262626]">{scenario.rent}</span>
                      </div>
                    )}
                    {scenario.profit && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estimated Profit</span>
                        <span className="font-bold text-[#DAA520]">{scenario.profit}</span>
                      </div>
                    )}
                    {scenario.cashFlow && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Cash Flow</span>
                        <span className="font-bold text-[#DAA520]">{scenario.cashFlow}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t-2 border-gray-100 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Estimated ROI</span>
                      <span className="text-3xl font-bold text-[#DAA520]">{scenario.roi}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-500 max-w-3xl mx-auto">
              *These are example scenarios for illustration purposes. Actual returns depend on market conditions, location, financing terms, and other factors. Schedule a consultation for personalized projections.
            </p>
          </div>
        </div>
      </section>

      {/* Why Partner with EC Home Builds */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Why Investors Choose EC Home Builds
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: 'Fixed-Price Contracts',
                description: 'Know your exact costs upfront. No surprises, no cost overruns. Accurate budgeting for reliable ROI calculations.'
              },
              {
                icon: PieChart,
                title: 'Proven Track Record',
                description: '11+ years of successful projects. We understand what sells and what rents in Central Ohio markets.'
              },
              {
                icon: TrendingUp,
                title: 'Market Expertise',
                description: 'Deep knowledge of Central Ohio submarkets, pricing, and rental demand. Build where opportunity exists.'
              },
              {
                icon: Users,
                title: 'Transparent Communication',
                description: 'Regular updates, accessible team, clear timelines. You always know where your investment stands.'
              },
              {
                icon: Home,
                title: 'Quality Construction',
                description: 'Premium materials and craftsmanship attract quality tenants and buyers, maximizing your returns.'
              },
              {
                icon: Building,
                title: 'Scalable Partnership',
                description: 'Start with one property and grow to a full portfolio. We support your expansion at every stage.'
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#DAA520]/10 rounded-full flex items-center justify-center mb-4">
                    <benefit.icon className="text-[#DAA520]" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Independence Duplex Spotlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#262626] mb-6">
                The Independence Duplex: Purpose-Built for Investors
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our most popular investment property. The Independence Duplex delivers strong cash flow, simplified management, and proven rental demand across Central Ohio markets.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Each unit: 1,885 sq. ft. with 3 beds, 2.5 baths',
                  'Separate utilities for simplified management',
                  'Attached 2-car garages attract quality tenants',
                  'Mirror layout reduces construction costs',
                  'Strong rental demand in Columbus market'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <TrendingUp className="text-[#DAA520] flex-shrink-0 mt-1" size={24} />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/floor-plans/independence-duplex">
                  <Button className="bg-[#262626] hover:bg-[#DAA520] text-white px-6 py-3">
                    View Duplex Details
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-2 border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-white px-6 py-3">
                    Request Duplex Pricing
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/8ez3v3g1znwt914/37d56fcee08a40fcad7ceda69d2079ee/optimized/tempImageAYcKTe.jpg"
                alt="The Independence Duplex"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Grow Your Portfolio?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Schedule a consultation to discuss your investment goals and explore opportunities in Central Ohio's growing market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Investment Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/cost-estimate">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Calculate Your ROI
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestorBuilds;
